import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PortalHeader from '../../../components/Portal/PortalHeader';
import { useAuth } from '../../../contexts/AuthContext/AuthContext';
import DocsService from '../../../services/DocsService';
import { buildTree, flattenTree, indexById, canMove } from '../../../services/docsTree';
import {
	docTypeCounts,
	docTypeIcon,
	filterDocsByType,
} from '../../../services/docsTemplates';
import MoveDocDialog from './MoveDocDialog';
import NewDocDialog from './NewDocDialog';
import './DocsList.css';

/* Docs — the landing surface. A nested tree (phase 2) with a flat fallback while
   searching: when you're looking for a doc by name, hierarchy is noise. */

function formatWhen(ts) {
	if (!ts?.toDate) return '';
	const d = ts.toDate();
	const now = new Date();
	if (d.toDateString() === now.toDateString()) {
		return `Today, ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
	}
	const days = Math.floor((now - d) / 86400000);
	if (days < 7) return d.toLocaleDateString(undefined, { weekday: 'long' });
	return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function DocsList() {
	const navigate = useNavigate();
	const { currentUser, userProfile } = useAuth();
	const [docs, setDocs] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [search, setSearch] = useState('');
	const [busy, setBusy] = useState(false);
	const [collapsed, setCollapsed] = useState(() => new Set());
	const [moving, setMoving] = useState(null); // the doc being moved
	const [typeFilter, setTypeFilter] = useState(null); // null = all types
	// {parentId, parentTitle} while the template picker is open
	const [creating, setCreating] = useState(null);

	useEffect(() => {
		const unsub = DocsService.subscribeToDocs(
			(rows) => {
				setDocs(rows);
				setLoading(false);
			},
			(err) => {
				console.error('[Docs] list failed', err);
				setError(err);
				setLoading(false);
			}
		);
		return unsub;
	}, []);

	const byId = useMemo(() => indexById(docs), [docs]);
	const searching = search.trim().length > 0;
	// Which type filters to OFFER — only the ones that would return something.
	const typeChips = useMemo(() => docTypeCounts(docs), [docs]);
	// Searching and filtering both flatten, so `flat` covers either.
	const flat = searching || !!typeFilter;

	const rows = useMemo(() => {
		/* A type filter flattens, exactly as search does: when you asked for "the
		   scripts", the tree is noise — and a filtered tree would have to either
		   drop matching docs whose parent doesn't match, or show parents that
		   aren't what you asked for. Neither is what "show me the scripts" means. */
		const scoped = filterDocsByType(docs, typeFilter);
		if (searching) {
			const q = search.toLowerCase().trim();
			return scoped
				.filter(
					(d) =>
						(d.title || '').toLowerCase().includes(q) ||
						(d.plainText || '').toLowerCase().includes(q)
				)
				.map((d) => ({ ...d, depth: 0, hasChildren: false }));
		}
		if (typeFilter) {
			return scoped.map((d) => ({ ...d, depth: 0, hasChildren: false }));
		}
		return flattenTree(buildTree(docs), collapsed);
	}, [docs, search, searching, collapsed, typeFilter]);

	const toggle = useCallback((id) => {
		setCollapsed((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}, []);

	const handleCreate = async (template) => {
		const parentId = creating?.parentId ?? null;
		setCreating(null);
		setBusy(true);
		try {
			const id = await DocsService.createDoc({
				uid: currentUser?.uid,
				displayName: userProfile?.displayName || currentUser?.displayName,
				parentId,
				template,
			});
			navigate(`/portal/work/docs/${id}`);
		} catch (err) {
			console.error('[Docs] create failed', err);
			setError(err);
			setBusy(false);
		}
	};

	const handleDelete = async (d) => {
		const kids = docs.filter((x) => x.parentId === d.id).length;
		const msg = kids
			? `Delete “${d.title || 'Untitled'}”?\n\nIts ${kids} sub-page${
					kids > 1 ? 's' : ''
			  } will move up a level, not be deleted.`
			: `Delete “${d.title || 'Untitled'}”?`;
		// eslint-disable-next-line no-alert
		if (!window.confirm(msg)) return;
		setBusy(true);
		try {
			await DocsService.deleteDocById(d.id);
		} catch (err) {
			console.error('[Docs] delete failed', err);
			setError(err);
		} finally {
			setBusy(false);
		}
	};

	const handleDuplicate = async (d) => {
		setBusy(true);
		try {
			await DocsService.duplicateDoc(d.id, {
				uid: currentUser?.uid,
				displayName: userProfile?.displayName || currentUser?.displayName,
			});
		} catch (err) {
			console.error('[Docs] duplicate failed', err);
			setError(err);
		} finally {
			setBusy(false);
		}
	};

	const handleMove = async (targetId) => {
		if (!moving) return;
		const check = canMove(moving.id, targetId, byId);
		if (!check.ok) return;
		setBusy(true);
		try {
			await DocsService.moveDoc(moving.id, targetId);
			setMoving(null);
		} catch (err) {
			console.error('[Docs] move failed', err);
			setError(err);
		} finally {
			setBusy(false);
		}
	};

	return (
		<div className='docs-list-page'>
			<PortalHeader title='Docs' subtitle='Planning, ideas, and working notes' />

			<div className='container py-4'>
				<div className='docs-toolbar'>
					<div className='docs-search'>
						<i className='fa-solid fa-magnifying-glass' aria-hidden='true'></i>
						<input
							type='text'
							placeholder='Search docs...'
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							aria-label='Search docs'
						/>
					</div>
					<button
						type='button'
						className='docs-new-btn'
						onClick={() => setCreating({ parentId: null })}
						disabled={busy}>
						<i className='fa-solid fa-plus' aria-hidden='true'></i>
						<span>New doc</span>
					</button>
				</div>

				{/* Type filter. Only rendered when there is more than one kind of doc
				    to choose between — a lone "All" chip is furniture, not a control. */}
				{typeChips.length > 1 && (
					<div className='docs-typefilter' role='group' aria-label='Filter by kind'>
						<button
							type='button'
							className={`docs-chip ${typeFilter === null ? 'is-active' : ''}`}
							aria-pressed={typeFilter === null}
							onClick={() => setTypeFilter(null)}>
							All
							<span className='docs-chip__count'>{docs.length}</span>
						</button>
						{typeChips.map((c) => (
							<button
								key={c.type}
								type='button'
								className={`docs-chip ${typeFilter === c.type ? 'is-active' : ''}`}
								aria-pressed={typeFilter === c.type}
								onClick={() => setTypeFilter(typeFilter === c.type ? null : c.type)}>
								<i className={`fa-solid ${c.icon}`} aria-hidden='true'></i>
								{c.label}
								<span className='docs-chip__count'>{c.count}</span>
							</button>
						))}
					</div>
				)}

				{error && (
					<div className='docs-error' role='alert'>
						{error.code === 'permission-denied'
							? 'Your account doesn’t have access to Docs.'
							: 'Something went wrong. Please try again.'}
					</div>
				)}

				{loading ? (
					<div className='docs-empty'>Loading…</div>
				) : rows.length === 0 ? (
					<div className='docs-empty'>
						{searching ? (
							/* A chip is only offered when it has docs, so a filter alone can
							   never empty the list — but a filter PLUS a search can, and
							   saying only "nothing matches" would look like the search was
							   broken rather than narrowed. */
							<p>
								No docs match “{search}”
								{typeFilter &&
									` in ${typeChips
										.find((c) => c.type === typeFilter)
										?.label.toLowerCase()}`}
								.
							</p>
						) : (
							<>
								<p className='docs-empty__lead'>No docs yet.</p>
								<p>
									Start one for lesson planning, a recital run-of-show, or anything
									you’d have opened Notion for.
								</p>
							</>
						)}
					</div>
				) : (
					<ul className='docs-tree'>
						{rows.map((d) => (
							<li
								key={d.id}
								className='docs-row'
								style={{ '--depth': flat ? 0 : d.depth }}>
								<span className='docs-row__twist'>
									{d.hasChildren ? (
										<button
											type='button'
											className='docs-row__toggle'
											aria-expanded={!collapsed.has(d.id)}
											aria-label={
												collapsed.has(d.id) ? 'Expand sub-pages' : 'Collapse sub-pages'
											}
											onClick={() => toggle(d.id)}>
											<i
												className={`fa-solid ${
													collapsed.has(d.id) ? 'fa-chevron-right' : 'fa-chevron-down'
												}`}></i>
										</button>
									) : (
										<span className='docs-row__leaf' aria-hidden='true' />
									)}
								</span>

								<button
									type='button'
									className='docs-row__main'
									onClick={() => navigate(`/portal/work/docs/${d.id}`)}>
									<span className='docs-row__title'>
										{/* Type icon: a script, a show flow and a planning page were
										    indistinguishable in this list until now. */}
										<i
											className={`fa-solid ${docTypeIcon(d)} docs-row__type`}
											aria-hidden='true'></i>
										{d.title || <em>Untitled</em>}
										{d.pinned && (
											<i
												className='fa-solid fa-thumbtack docs-row__pin'
												aria-label='Pinned'></i>
										)}
									</span>
									<span className='docs-row__meta'>
										{formatWhen(d.updatedAt)}
										{d.mentions?.length > 0 && (
											<span className='docs-row__tags'>
												<i className='fa-solid fa-at' aria-hidden='true'></i>
												{d.mentions.length}
											</span>
										)}
									</span>
								</button>

								<span className='docs-row__actions'>
									<button
										type='button'
										title='New sub-page'
										aria-label={`New sub-page under ${d.title || 'Untitled'}`}
										onClick={() => setCreating({ parentId: d.id, parentTitle: d.title })}
										disabled={busy}>
										<i className='fa-solid fa-plus'></i>
									</button>
									<button
										type='button'
										title='Duplicate'
										aria-label={`Duplicate ${d.title || 'Untitled'}`}
										onClick={() => handleDuplicate(d)}
										disabled={busy}>
										<i className='fa-solid fa-copy'></i>
									</button>
									<button
										type='button'
										title='Move'
										aria-label={`Move ${d.title || 'Untitled'}`}
										onClick={() => setMoving(d)}
										disabled={busy}>
										<i className='fa-solid fa-arrow-right-arrow-left'></i>
									</button>
									<button
										type='button'
										title='Delete'
										aria-label={`Delete ${d.title || 'Untitled'}`}
										onClick={() => handleDelete(d)}
										disabled={busy}>
										<i className='fa-solid fa-trash'></i>
									</button>
								</span>
							</li>
						))}
					</ul>
				)}
			</div>

			{creating && (
				<NewDocDialog
					parentTitle={creating.parentTitle}
					onCancel={() => setCreating(null)}
					onCreate={handleCreate}
				/>
			)}

			{moving && (
				<MoveDocDialog
					doc={moving}
					docs={docs}
					onCancel={() => setMoving(null)}
					onMove={handleMove}
				/>
			)}
		</div>
	);
}

export default DocsList;
